import { useCallback, useEffect } from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import {
  LoadableButton,
  Box,
  Text,
  H2,
  Grid,
  GridCell,
  Image,
  Container,
  Alert
} from '../../../components';
import { replaceRoute } from '../../../components/Link';
import { useValue } from '../../CheckoutValueContext';
import {
  productBySlugQuery_productBySlug,
  CurrentUser,
  redeemValueCodeExisitingUser,
  redeemValueCodeExisitingUserVariables,
  currentUserQuery
} from '../../../core-types';
import { CURRENT_USER_FRAGMENT } from '../../../lib/core/fragments/currentUser';
import { CURRENT_USER_QUERY } from '../../../lib/core/useCurrentUser';
import { INTERCOM_SETTINGS_QUERY } from '../../../lib/with-auth';
import { useUserShouldOnboard } from '../../Onboarding/useOnboardingSteps';
import { gql, useMutation } from '@apollo/client';

type Props = {
  product: productBySlugQuery_productBySlug;
  currentUser: CurrentUser | null;
  intl: IntlShape;
};

const ompletePurchaseButton = (
  <FormattedMessage id="redeem" defaultMessage="Redeem" />
);

const REDEEM_VALUE_CODE_EXISTING_USER_MUTATION = gql`
  ${CURRENT_USER_FRAGMENT}
  mutation redeemValueCodeExisitingUser(
    $valueCode: String!
    $signupConsent: SignupConsent
  ) {
    redeemValueCodeExisitingUser(
      valueCode: $valueCode
      signupConsent: $signupConsent
    ) {
      user {
        ...CurrentUser
      }
    }
  }
`;

const SummaryStep = ({ product, intl, currentUser }: Props) => {
  const [state] = useValue();
  const { validCoupon } = state;
  const { userRequiresOnboarding } = useUserShouldOnboard(currentUser);
  const onValueCodeRedeemed = useCallback(() => {
    if (userRequiresOnboarding) {
      replaceRoute({
        route: 'post-checkout',
        intl
      });
    } else {
      replaceRoute({ route: 'profile', intl });
    }
  }, [userRequiresOnboarding]);

  const agreedToTerms = currentUser?.agreedToTerms;

  useEffect(() => {
    if (!agreedToTerms || !currentUser || !validCoupon) {
      const timeoutId = setTimeout(() => {
        replaceRoute({
          intl,
          route: 'redeem-coupon'
        });
      }, 1500);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [agreedToTerms, currentUser, validCoupon]);

  const [redeemValueCode, { error, loading, client }] = useMutation<
    redeemValueCodeExisitingUser,
    redeemValueCodeExisitingUserVariables
  >(REDEEM_VALUE_CODE_EXISTING_USER_MUTATION, {
    onCompleted: data => {
      if (data.redeemValueCodeExisitingUser) {
        onValueCodeRedeemed();
      }
    },
    update: (cache, { data }) => {
      if (!data || !data.redeemValueCodeExisitingUser) {
        return;
      }
      const { user } = data.redeemValueCodeExisitingUser;
      if (!user) {
        return;
      }

      const currentUserQueryResult = cache.readQuery<currentUserQuery>({
        query: CURRENT_USER_QUERY
      });

      cache.writeQuery<currentUserQuery>({
        query: CURRENT_USER_QUERY,
        data: {
          intercomSettings:
            (currentUserQueryResult &&
              currentUserQueryResult.intercomSettings) ||
            null,
          currentUser: user
        }
      });

      client.query({
        query: INTERCOM_SETTINGS_QUERY,
        fetchPolicy: 'network-only'
      });
    }
  });

  //Since we added the user consent update before checkout, we don't need this anymore
  // if (!agreedToTerms) {
  //   return (
  //     <Alert variant="error" my="sm" data-testid="SummaryStep_No_Tos_Accept">
  //       <FormattedMessage
  //         id="modules.BuyProduct.SummaryStep.missingTosAccept"
  //         defaultMessage="Missing terms of service acceptance, please wait …"
  //       />
  //     </Alert>
  //   );
  // }

  if (!currentUser) {
    return (
      <Alert variant="error" my="sm" data-testid="SummaryStep_No_Token">
        <FormattedMessage
          id="modules.BuyProduct.SummaryStep.missingToken"
          defaultMessage="Missing verification token, please wait …"
        />
      </Alert>
    );
  }

  if (!validCoupon) {
    return (
      <Alert variant="error">
        <FormattedMessage
          id="modules.RedeemCoupon.SummaryStep.invalidData"
          defaultMessage="No valid coupon provided, please wait …"
        />
      </Alert>
    );
  }

  return (
    <Container variant="wide" bleed>
      <Grid justifyContent="center">
        {product && product.image && product.image.url && (
          <GridCell width={[1, 1, 1, 2 / 5]}>
            <Image
              boxShadow="heavy"
              borderRadius="lg"
              src={product.image.url}
            />
          </GridCell>
        )}
        <GridCell
          width={[1, 1, 1, 3 / 5]}
          px={[3, 3, 3, 6]}
          flexDirection="column"
          height="100%"
        >
          <Box flex={1} mb="md">
            {error && (
              <Alert variant="error">
                <FormattedMessage
                  id="modules.RedeemCoupon.SummaryStep.error"
                  defaultMessage="Could not redeem the voucher code. Please try again later."
                />
              </Alert>
            )}
            <Text mt={[4, 4, 4, 0]}>
              <FormattedMessage id="summary" defaultMessage="Summary" />
            </Text>
            <H2 mb="xs" typoStyle="md">
              {product && product.name}
            </H2>
            <Text as="p" mb="sm">
              <FormattedMessage
                id="modules.RedeemCoupon.SummaryStep.summary"
                defaultMessage="By continuing, you redeem the voucher code {voucherCode} to a subscription on the phone number {phoneNumber}"
                values={{
                  voucherCode: validCoupon,
                  phoneNumber: currentUser.parsedPhoneNumber.formattedNumber
                }}
              />
            </Text>
            <LoadableButton
              variant="primary"
              shape="cool"
              onClick={async () => {
                redeemValueCode({
                  variables: {
                    valueCode: validCoupon,
                    signupConsent: {
                      info: false,
                      autoRenew: false,
                      allowCharges: false
                    }
                  }
                });
              }}
              loading={loading}
              responseState={error ? 'error' : 'success'}
              data-testid="Redeem_ValueCode_Button"
            >
              {ompletePurchaseButton}
            </LoadableButton>
          </Box>
        </GridCell>
      </Grid>
    </Container>
  );
};

export default SummaryStep;
